/**
 * Derive a single view-model the Dashboard can render plan/billing info from.
 *
 * Input: the catalog + subscription returned by `usePricing()` from
 * @d-m-organization/pricing-ui (already mounted in App.tsx via PricingProvider).
 *
 * Output: a `PlanView` shape with display strings + UI flags. Returns null
 * while the catalog is still loading; the component renders skeletons.
 *
 * See docs/HD-98/DESIGN.md for the full algorithm and rationale.
 */

import type {
  BillingInterval,
  CatalogBundle,
  CatalogData,
  CatalogProduct,
  ProductTier,
  SubscriptionState,
} from "@d-m-organization/pricing-ui";
import { formatEuro } from "./formatEuro.js";

// ───────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────

export type PlanView = {
  /** Human-readable plan label, e.g. "Free", "Pro", "Business". */
  readonly planLabel: string;
  /**
   * Seat count copy. Hardcoded "1 seat" today — HD-100 (Invite teammate)
   * introduces the real seats data source. Comment also lives at the use site.
   */
  readonly seatLabel: string;
  /** Number of products the user has at tier > "free". */
  readonly unlockedCount: number;
  /** Total visible products in the catalog (replaces magic number "6"). */
  readonly totalProductCount: number;
  /**
   * Savings in whole euros if the user upgraded from current state to the
   * cheapest visible bundle. `null` when no bundle is cheaper, no bundle
   * exists, or the user is already on a bundle.
   */
  readonly bundleSavingsEur: number | null;
  /**
   * Upgrade CTA copy + chosen interval, or null when CTA should be hidden
   * (user is already fully covered by a bundle subscription).
   */
  readonly upgradeCta: {
    readonly label: string;
    readonly interval: BillingInterval;
  } | null;
};

// ───────────────────────────────────────────────────────────────────────────
// Public API
// ───────────────────────────────────────────────────────────────────────────

/**
 * Derive the dashboard's plan view from catalog + subscription.
 *
 * @returns `null` when `catalog` is null (loading state — caller renders
 *   skeletons). Always returns a usable shape once catalog is present, even
 *   when the subscription hasn't loaded or errored — falls through to the
 *   "free user" view in that case so the dashboard never crashes.
 */
export function derivePlanView(
  catalog: CatalogData | null,
  subscription: SubscriptionState | null,
): PlanView | null {
  if (!catalog) return null;

  const visible = catalog.products.filter((p) => p.visible);
  const totalProductCount = visible.length;

  // ── Unlocked count ──
  const entitlements = subscription?.entitlements ?? {};
  const unlockedCount = visible.filter((p) => {
    const e = entitlements[p.productId];
    return e != null && e.tier !== "free";
  }).length;

  // ── Plan label + seat label ──
  const ecosystemActive =
    !!subscription?.ecosystem &&
    (subscription.ecosystem.status === "active" ||
      subscription.ecosystem.status === "trialing");

  let planLabel: string;
  // The pricing-ui type declares `ecosystem.plan: string`, but the Hub API
  // omits the field for trialing/active ecosystems without an assigned
  // bundle (e.g. ecosystem trial state with only individual product subs).
  // Defensively coerce the missing case to the "highest paid product tier"
  // path so the dashboard never crashes on `plan.replace(...)`.
  const ecosystemPlan = subscription?.ecosystem?.plan;
  if (ecosystemActive && subscription?.ecosystem && ecosystemPlan) {
    planLabel = ecosystemPlanLabel(ecosystemPlan);
  } else {
    const highest = highestPaidTier(subscription?.products ?? []);
    planLabel = highest ? tierLabel(highest) : "Free";
  }

  // HD-100: seat count is hardcoded; the Invite-teammate ticket introduces
  // the real source. Keep this as a single source of truth so when seats
  // ship, only this file (and `useSeats()` later) needs to change.
  const seatLabel = "1 seat";

  // ── Coverage check: is the user on a bundle that covers every visible product? ──
  const ecosystemProducts = new Set(subscription?.ecosystem?.products ?? []);
  const fullyCovered =
    ecosystemActive &&
    visible.every((p) => ecosystemProducts.has(p.productId));

  // ── Upgrade CTA ──
  // Default to monthly until the Hub starts returning interval on
  // EcosystemSubscription (the field is reserved as `BillingInterval | null`
  // on the type today — `?? "month"` covers both null and undefined).
  const interval: BillingInterval =
    subscription?.ecosystem?.interval ?? "month";

  const upgradeCta = fullyCovered
    ? null
    : buildUpgradeCta(catalog, interval);

  // ── Free→bundle savings ──
  const bundleSavingsEur = ecosystemActive
    ? null
    : computeFreeToBundleSavingsEur(visible, catalog.bundles);

  return {
    planLabel,
    seatLabel,
    unlockedCount,
    totalProductCount,
    bundleSavingsEur,
    upgradeCta,
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers (exported for tests; not part of the public API)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Map an ecosystem `plan` string to a display label.
 * Falls through to title-casing the plan id with `_bundle` suffix stripped.
 *
 * Accepts `string | undefined | null` even though the pricing-ui type
 * declares `plan: string` — the Hub API omits the field for ecosystems
 * in a trial without an assigned bundle. Empty input returns "Free".
 */
export function ecosystemPlanLabel(plan: string | undefined | null): string {
  if (plan == null) return "Free";
  switch (plan) {
    case "free":
      return "Free";
    case "pro_bundle":
      return "Pro";
    case "business_bundle":
      return "Business";
    default: {
      const stripped = plan.replace(/_bundle$/, "");
      return stripped.length > 0
        ? stripped[0]!.toUpperCase() + stripped.slice(1)
        : "Plan";
    }
  }
}

/** Capitalise a tier name. */
export function tierLabel(tier: ProductTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/**
 * Pick the highest tier across a user's active product subscriptions.
 * Returns `null` if none are active/paid.
 */
export function highestPaidTier(
  products: SubscriptionState["products"],
): ProductTier | null {
  const order: Record<ProductTier, number> = { free: 0, pro: 1, business: 2 };
  let best: ProductTier | null = null;
  for (const sub of products) {
    if (sub.status !== "active" && sub.status !== "trialing") continue;
    if (sub.tier === "free") continue;
    if (!best || order[sub.tier] > order[best]) {
      best = sub.tier;
    }
  }
  return best;
}

/**
 * Build the upgrade CTA. Historically pointed at the cheapest visible bundle;
 * with bundles deprecated (HD-???), this returns null whenever the catalog
 * has no visible bundles — which is now the steady-state expectation. The
 * Dashboard hides its upgrade button when this is null.
 *
 * Per-product upgrade CTAs are surfaced inside each product's own UI (or
 * via the Pricing page), not from this view-model.
 */
export function buildUpgradeCta(
  catalog: CatalogData,
  interval: BillingInterval,
): PlanView["upgradeCta"] {
  const bundle = pickPrimaryBundle(catalog.bundles);
  if (!bundle) return null;

  const monthlyEquivCents =
    interval === "year"
      ? Math.round(bundle.annualPriceCents / 12)
      : bundle.monthlyPriceCents;

  return {
    label: `Upgrade to ${tierLabel(bundle.grantedTier)} — €${formatEuro(monthlyEquivCents)}/mo`,
    interval,
  };
}

/**
 * Pick the visible bundle with the lowest sortOrder (the "primary" upsell).
 * Ties are broken by `bundleId` for deterministic output across runs and
 * across users who land on the same catalog snapshot.
 */
export function pickPrimaryBundle(
  bundles: readonly CatalogBundle[],
): CatalogBundle | null {
  const visible = bundles.filter((b) => b.visible);
  if (visible.length === 0) return null;
  return [...visible].sort(
    (a, b) =>
      a.sortOrder - b.sortOrder || a.bundleId.localeCompare(b.bundleId),
  )[0]!;
}

/**
 * For a free user, compute the monthly euro savings of switching to the
 * cheapest visible bundle vs. paying à-la-carte for every product the
 * bundle includes, at the `pro` tier. Returns null when no bundle exists
 * or the bundle isn't strictly cheaper than the equivalent singles total.
 *
 * Important: only products in `bundle.includedProducts` count toward the
 * singles total. A bundle that covers 4 of 6 products must NOT claim
 * savings against the full 6-product spend.
 */
export function computeFreeToBundleSavingsEur(
  visibleProducts: readonly CatalogProduct[],
  bundles: readonly CatalogBundle[],
): number | null {
  const bundle = pickPrimaryBundle(bundles);
  if (!bundle) return null;

  const includedSet = new Set(bundle.includedProducts);
  const singlesTotalCents = visibleProducts
    .filter((p) => includedSet.has(p.productId))
    .reduce((sum, product) => {
      const proTier = product.tiers["pro"];
      if (!proTier?.active) return sum;
      return sum + proTier.monthlyPriceCents;
    }, 0);

  const savingsCents = singlesTotalCents - bundle.monthlyPriceCents;
  if (savingsCents <= 0) return null;

  // Round to whole euros for the pill copy ("SAVE €18/MO …")
  return Math.round(savingsCents / 100);
}
